import React from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, PencilIcon } from '@heroicons/react/24/outline';

const ViewJournalEntryModal = ({ entry, onClose, onEdit }) => {
  const { t } = useTranslation();

  if (!entry) return null;

  // Entry types configuration
  const entryTypes = {
    manual: { name: t('journal.manual', 'Manual Entry'), icon: '✏️', color: 'blue' },
    automatic: { name: t('journal.automatic', 'Automatic Entry'), icon: '🤖', color: 'green' },
    adjustment: { name: t('journal.adjustment', 'Adjustment'), icon: '⚖️', color: 'yellow' },
    accrual: { name: t('journal.accrual', 'Accrual'), icon: '📅', color: 'purple' },
    correction: { name: t('journal.correction', 'Correction'), icon: '🔧', color: 'orange' },
    closing: { name: t('journal.closing', 'Closing'), icon: '🔒', color: 'red' },
    reversal: { name: t('journal.reversal', 'Reversal'), icon: '↩️', color: 'gray' },
    reclassification: { name: t('journal.reclassification', 'Reclassification'), icon: '🔄', color: 'indigo' }
  };

  const entryTypeInfo = entryTypes[entry.entryType] || entryTypes.manual;

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { class: 'badge badge-warning', text: t('quotations.draft') },
      posted: { class: 'badge badge-success', text: t('accounting.posted') },
      reversed: { class: 'badge badge-error', text: t('accounting.reversed', 'Reversed') }
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    return <span className={config.class}>{config.text}</span>;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('journal.view_entry', 'View Journal Entry')}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {entry.entryNumber} • {new Date(entry.date).toLocaleDateString()}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {entry.status === 'draft' && onEdit && (
                <button
                  onClick={onEdit}
                  className="btn btn-sm btn-primary"
                >
                  <PencilIcon className="w-4 h-4 mr-1" />
                  {t('journal.edit', t('common.edit'))}
                </button>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Entry Header Info */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  {t('accounting.entry_type')}
                </label>
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{entryTypeInfo.icon}</span>
                  <span className="font-medium text-gray-900">{entryTypeInfo.name}</span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  {t('accounting.table.status')}
                </label>
                <div>{getStatusBadge(entry.status)}</div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  {t('accounting.total_amount')}
                </label>
                <div className="text-lg font-bold text-blue-600">
                  SAR {(entry.totalDebit || 0).toFixed(2)}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  {t('accounting.balance_check')}
                </label>
                <div className="flex items-center space-x-2">
                  <span className="text-green-600 text-lg">✅</span>
                  <span className="text-sm font-medium text-green-600">
                    {t('accounting.balanced')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Entry Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3">
                {t('journal.entry_details', 'Entry Details')}
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    {t('chart.date')}
                  </label>
                  <div className="text-sm font-medium text-gray-900">
                    {new Date(entry.date).toLocaleDateString()}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    {t('accounting.description')}
                  </label>
                  <div className="text-sm text-gray-900">
                    {entry.description}
                  </div>
                </div>

                {entry.reference && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      {t('accounting.reference')}
                    </label>
                    <div className="text-sm font-mono text-gray-900">
                      {entry.reference}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3">
                {t('accounting.audit_trail')}
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    {t('journal.created_by', 'Created By')}
                  </label>
                  <div className="text-sm text-gray-900">
                    {entry.createdBy || t('common.unknown', 'Unknown')}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    {t('journal.created_at', 'Created At')}
                  </label>
                  <div className="text-sm text-gray-900">
                    {entry.createdAt ? new Date(entry.createdAt.toDate()).toLocaleString() : t('common.not_available', 'Not available')}
                  </div>
                </div>

                {entry.postedAt && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      {t('journal.posted_at', 'Posted At')}
                    </label>
                    <div className="text-sm text-gray-900">
                      {new Date(entry.postedAt.toDate()).toLocaleString()}
                    </div>
                  </div>
                )}

                {entry.reversedAt && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      {t('journal.reversed_at', 'Reversed At')}
                    </label>
                    <div className="text-sm text-gray-900">
                      {new Date(entry.reversedAt.toDate()).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('accounting.line_items')}
            </h3>
            
            <div className="overflow-x-auto border rounded-lg">
              <table className="table table-zebra w-full">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left">{t('journal.account', 'Account')}</th>
                    <th className="text-left">{t('accounting.description')}</th>
                    <th className="text-right">{t('accounting.debit')}</th>
                    <th className="text-right">{t('accounting.credit')}</th>
                  </tr>
                </thead>
                <tbody>
                  {entry.lineItems?.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <div className="font-medium text-sm">
                          {item.accountCode} - {item.accountName}
                        </div>
                      </td>
                      <td>
                        <div className="text-sm text-gray-900">
                          {item.description}
                        </div>
                      </td>
                      <td className="text-right">
                        {(item.debit || 0) > 0 ? (
                          <span className="font-semibold text-green-600">
                            {(item.debit || 0).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="text-right">
                        {(item.credit || 0) > 0 ? (
                          <span className="font-semibold text-red-600">
                            {(item.credit || 0).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-medium">
                    <td colSpan="2" className="text-right">{t('journal.totals', 'Totals')}</td>
                    <td className="text-right text-green-600 font-bold">
                      SAR {(entry.totalDebit || 0).toFixed(2)}
                    </td>
                    <td className="text-right text-red-600 font-bold">
                      SAR {(entry.totalCredit || 0).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Source Information */}
          {(entry.sourceType || entry.autoGenerated) && (
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <h3 className="font-medium text-yellow-900 mb-2">
                {t('journal.source_information', 'Source Information')}
              </h3>
              <div className="text-sm text-yellow-800">
                {entry.autoGenerated && (
                  <div className="flex items-center space-x-2 mb-1">
                    <span>🤖</span>
                    <span>{t('accounting.auto_generated')}</span>
                  </div>
                )}
                {entry.sourceType && (
                  <div className="flex items-center space-x-2">
                    <span>📄</span>
                    <span>
                      {t('journal.source_type', 'Source')}: {entry.sourceType}
                      {entry.sourceId && ` (ID: ${entry.sourceId})`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Reversal Information */}
          {entry.status === 'reversed' && entry.reversalEntryId && (
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <h3 className="font-medium text-red-900 mb-2">
                {t('journal.reversal_information', 'Reversal Information')}
              </h3>
              <div className="text-sm text-red-800">
                <div className="flex items-center space-x-2 mb-1">
                  <span>↩️</span>
                  <span>{t('accounting.entry_reversed')}</span>
                </div>
                <div>
                  {t('journal.reversal_entry', 'Reversal Entry ID')}: {entry.reversalEntryId}
                </div>
                {entry.reversedBy && (
                  <div>
                    {t('journal.reversed_by', 'Reversed By')}: {entry.reversedBy}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {t('journal.table.entry_id')}: {entry.entryNumber}
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-outline"
              >
                {t('common.close', t('common.close'))}
              </button>
              {entry.status === 'draft' && onEdit && (
                <button
                  type="button"
                  onClick={onEdit}
                  className="btn btn-primary"
                >
                  <PencilIcon className="w-4 h-4 mr-1" />
                  {t('journal.edit_entry', 'Edit Entry')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewJournalEntryModal; 